import { Text } from 'common/alerts/components/alerts';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Button, Div, Flex, Icon, notification, SubTitle } from 'ui';
import { orderStatus } from 'utils/app-configs';
import PermissionCheck from '../../common/features/permission-check';
import AddSuborderDialog from '../field-service-engineer/add-suborder-dialog';
import SuborderTable from '../field-service-engineer/suborder-table';

const engineerType = {
  name: PropTypes.string,
  number: PropTypes.string,
};

const SeverityIcon = styled(Icon)`
  color: ${p => p.theme.colors.alerts[p.severity]};
  margin-right: 10px;
`;

const WarningText = styled(Text)`
  margin-left: 50px;
  font-size: 15px;
`;

const SubOrder = ({
  engineer,
  preferredEngineer,
  lastVisitEngineer,
  subOrders,
  onAddSubOrder,
  onOpenSubOrder,
  form,
  disabled,
  activeStatus,
  cannotMovePastMainOrder,
  dirty,
  intl,
  hasError,
  isLocked,
}) => {
  const [showSubOrderDialog, setShowSubOrderDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddSubOrder = (technicianNumber, technicianName) => {
    onAddSubOrder(technicianNumber, technicianName);
    setIsLoading(true);
  };
  const handleClose = () => {
    setIsLoading(false);
    setShowSubOrderDialog(false);
  };
  const handleOpen = () => {
    if (dirty) {
      notification.error({
        message: intl.formatMessage({ id: 'order.fieldEngineer.alerts.UNSAVED_CHANGES' }),
        description: intl.formatMessage({ id: 'order.fieldEngineer.alerts.PLEASE_CHANGE' }),
        className: 'extended-notification',
        duration: 0,
        placement: 'top',
      });
    } else if (
      form.getFieldInstance('customerPurchaseOrder') &&
      !form.getFieldValue('customerPurchaseOrder')
    ) {
      notification.warning({
        message: intl.formatMessage({ id: 'order.fieldEngineer.alerts.PO_NUMBER_REQ' }),
        description: intl.formatMessage({ id: 'order.fieldEngineer.alerts.PO_NUMBER_REQ_DESC' }),
        className: 'extended-notification',
        duration: 0,
        placement: 'top',
      });
    } else {
      setIsLoading(false);
      setShowSubOrderDialog(true);
    }
  };

  const canAddSubOrder = () => {
    // If the order is disabled, you can't add a suborder
    // A suborder can be created when a main order is locked
    if (disabled && !isLocked) {
      return false;
    }

    // If the order is past CNF, you can't add a suborder
    // In all other cases, you can add a suborder.
    return !(activeStatus && activeStatus.id > orderStatus.executed);
  };

  const transformValues = (values) => {
    const defaultEngineer = preferredEngineer || lastVisitEngineer;
    return values?.map((v) => {
      let { name } = v;
      if (defaultEngineer && v.number === defaultEngineer.number) {
        name = `${name} ${intl.formatMessage({ id: 'order.groupDropdown.DEFAULT' })} `;
      }
      return { ...v, name };
    });
  };

  const defaultEngineer = preferredEngineer || lastVisitEngineer;

  const groups = [
    {
      id: 'regions',
      name: intl.formatMessage({ id: 'order.groupDropdown.FIRST_GROUP' }),
      filter: workcenter => workcenter.isRegion,
    },
    {
      id: 'engineers',
      name: intl.formatMessage({ id: 'order.groupDropdown.SECOND_GROUP' }),
      filter: workcenter => !workcenter.isRegion,
    },
  ];

  return (
    <Div marginTop={45}>
      <SubTitle>
        {intl.formatMessage({ id: 'order.SUBORDERS' })}
        {cannotMovePastMainOrder &&
          <WarningText severity={2}>
            <SeverityIcon type='exclamation-triangle' severity={2} />
            <FormattedMessage id='order.fieldEngineer.alerts.PASSED_MAIN' />
          </WarningText>
        }
      </SubTitle>
      <SuborderTable onOpenSubOrder={onOpenSubOrder} subOrders={subOrders} />
      <Flex marginBottom={25} marginTop={10}>
        <PermissionCheck permissionsList={['orderrelease']}>
          <Div marginRight={10}>
            <Button
              type='secondary'
              onClick={handleOpen}
              disabled={!canAddSubOrder()}
            >
              <FormattedMessage id='order.fieldEngineer.addSubOrderModal.ADD_SUBORDER' />{'...'}
            </Button>
          </Div>
        </PermissionCheck>
      </Flex>
      {hasError ||
        <AddSuborderDialog
          onAddSubOrder={handleAddSubOrder}
          visible={showSubOrderDialog && canAddSubOrder()}
          onClose={handleClose}
          isLoading={isLoading}
          defaultEngineer={defaultEngineer}
          engineer={engineer || defaultEngineer}
          groups={groups}
          transformValues={transformValues}
        />
      }
    </Div>
  );
};

SubOrder.propTypes = {
  engineer: PropTypes.shape(engineerType),
  preferredEngineer: PropTypes.shape(engineerType),
  lastVisitEngineer: PropTypes.shape(engineerType),
  subOrders: PropTypes.array,
  onAddSubOrder: PropTypes.func,
  onOpenSubOrder: PropTypes.func,
  form: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  activeStatus: PropTypes.object,
  cannotMovePastMainOrder: PropTypes.bool,
  dirty: PropTypes.bool,
  intl: PropTypes.object,
  hasError: PropTypes.bool,
  isLocked: PropTypes.bool,
};


export default injectIntl(SubOrder);
